import * as React from 'react';
import { Property } from '../../../../../api/Property';
import LoadingOverlay from '../../../../common/LoadingOverlay';
import { SyntheticEvent } from 'react';
import { OnfidoInput } from './OnfidoInput';
import { OnfidoIdVerificationStatuses } from '../../../../../utilities/Enums';
import ProgressBar from '../../../../common/ProgressBar';
import {
    FailedReport,
    getOnfidoCheckStatus,
    startOnfidoIdCheck,
    updateOnfidoApplicant
} from '../../../../../api/IdVerification';
import { getProspectUserProfile } from '../../../../../api/ProspectUserProfile';
import OnfidoBirthdayForm from './OnfidoBirthdayForm';

export interface Props {
    propertyInfo: Property;
    guestCardGuid?: string;
    goForward(): void;
    updateVerificationStatus(success: boolean): void;
}

interface State {
    isLoading: boolean;
    errorMessage?: string;
    wasFormValidated: boolean;
    intervalId?: number;
    sdkToken?: string;
    checkStatus: OnfidoIdVerificationStatuses;
    failedReason?: FailedReport[];
    warningText?: string;
    prospectUserPhone?: string;
    setBirthDateIsVisible: boolean;
}

class OnfidoVerification extends React.Component<Props, State> {
    formControl: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);

        this.state = {
            isLoading: false,
            wasFormValidated: false,
            checkStatus: OnfidoIdVerificationStatuses.OnFirstCheck,
            setBirthDateIsVisible: true
        };

        this.onSubmit = this.onSubmit.bind(this);
        this.loadPhoneNumber = this.loadPhoneNumber.bind(this);
        this.loadCheckStatus = this.loadCheckStatus.bind(this);
        this.updateOrCreateOnfidoApplication = this.updateOrCreateOnfidoApplication.bind(this);
        this.startIdCheck = this.startIdCheck.bind(this);
    }

    async componentWillUnmount() {
        if (this.state.intervalId !== null) {
            window.clearInterval(this.state.intervalId);
        }
    }

    async componentDidMount() {
        if (this.state.checkStatus === OnfidoIdVerificationStatuses.WaitingForResult) {
            this.loadPhoneNumber();
            this.setState({
                ...this.state,
                intervalId: window.setInterval(() => this.loadCheckStatus(), 5000)
            });
        }
    }

    async componentDidUpdate(prevProps: Props, prevState: State) {
        if (
            prevState.checkStatus !== this.state.checkStatus &&
            this.state.checkStatus === OnfidoIdVerificationStatuses.WaitingForResult
        ) {
            this.loadCheckStatus();
            this.setState({
                ...this.state,
                intervalId: window.setInterval(() => this.loadCheckStatus(), 5000)
            });
        }
        if (
            this.state.intervalId !== null &&
            this.state.checkStatus !== OnfidoIdVerificationStatuses.WaitingForResult
        ) {
            window.clearInterval(this.state.intervalId);
        }
    }

    render() {
        const failedDisplay = (
            <p>
                Unfortunately, we are unable to verify your identity for one or more of the
                following reasons
                <br />
                1. Poor Image Quality
                <br />
                2. Unsupported Document
                <br />
                3. Expired Document
                <br />
                Please contact 888-297-7411 or support@tour24.io for further assistance. Thank
                you.
            </p>
        );
        let currentPageDisplay: JSX.Element;

        switch (this.state.checkStatus) {
            case OnfidoIdVerificationStatuses.ExpirationCaptureFailed:
                currentPageDisplay = failedDisplay;
                break;
            case OnfidoIdVerificationStatuses.FailedBothChecks:
                // can't try again screen
                currentPageDisplay = failedDisplay;
                break;
            case OnfidoIdVerificationStatuses.CheckPassed:
                // Already passed check, don't need to try again
                currentPageDisplay = (
                    <div>
                        <p>
                            You have passed the identity verification check. Please click{' '}
                            <b>Next</b> to continue signing up!
                        </p>
                    </div>
                );
                break;
            case OnfidoIdVerificationStatuses.WaitingForResult:
                currentPageDisplay = (
                    <div>
                        <p>
                            Thank you for uploading documents. Please wait while we confirm your
                            identity. This may take 2 to 5 minutes on average.
                        </p>
                        <ProgressBar />
                    </div>
                );
                break;
            case OnfidoIdVerificationStatuses.FailedFirstCheckOnSecond:
                // Update any relevant state with failed first check
                if (this.state.sdkToken) {
                    currentPageDisplay = (
                        <div>
                            <p>
                                Your first identity check failed or has expired. Please try
                                uploading a different set of documents.
                            </p>
                            <OnfidoInput
                                sdkToken={this.state.sdkToken}
                                onComplete={this.startIdCheck}
                                phoneNumber={this.state.prospectUserPhone}
                            />
                        </div>
                    );
                    // Display Onfido element
                } else {
                    currentPageDisplay = (
                        <p>Please enter your birthdate to start ID verification.</p>
                    );
                }
                break;
            case OnfidoIdVerificationStatuses.OnFirstCheck:
                if (this.state.sdkToken) {
                    currentPageDisplay = (
                        <div>
                            <p>
                                The verification process will require the taking of a selfie. When
                                you arrive on that screen, scroll all the way down and tap the take
                                photo button. If you see a "Glare Detected" message, you can safely
                                ignore the message and proceed.
                            </p>
                            <p>
                                The approval process may take 2 to 5 minutes on average to complete.
                            </p>
                            <OnfidoInput
                                sdkToken={this.state.sdkToken}
                                onComplete={this.startIdCheck}
                                phoneNumber={this.state.prospectUserPhone}
                            />
                        </div>
                    );
                    // Display Onfido element
                } else {
                    currentPageDisplay = (
                        <p>Please enter your birthdate to start ID verification.</p>
                    );
                }
                break;
            case OnfidoIdVerificationStatuses.ErrorOccurred:
                // Error occurred
                // Try again if user hasn't failed both checks already?
                currentPageDisplay = (
                    <p>An error occurred during the process. Please refresh the page.</p>
                );
                break;
            default:
                currentPageDisplay = <p>Please refresh the page.</p>;
        }

        return (
            <div>
                <LoadingOverlay show={this.state.isLoading} inBootstrapColumn={true} />
                <div className="onfido-id-verify-form clearfix">
                    {this.state.sdkToken !== undefined && currentPageDisplay}
                    {this.state.errorMessage && (
                        <p className="text-danger">{this.state.errorMessage}</p>
                    )}
                    {this.state.failedReason && (
                        <div className="alert alert-danger" style={{ fontSize: '12px' }} role="alert">
                            {this.state.failedReason.map(r => (
                                <>
                                    <div>{r.reportType} report failed the following sections:</div>
                                    <ul>
                                        {r.failedBreakdowns.map(b => (
                                            <li>
                                                {b.breakdownType}
                                                <ul>
                                                    {b.failedSections.map(s => (
                                                        <li>{s}</li>
                                                    ))}
                                                </ul>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ))}
                        </div>
                    )}
                    {this.state.checkStatus !== OnfidoIdVerificationStatuses.CheckPassed &&
                        this.state.checkStatus !== OnfidoIdVerificationStatuses.WaitingForResult &&
                        this.state.setBirthDateIsVisible &&
                        (this.props.guestCardGuid ? (
                            <OnfidoBirthdayForm
                                updateOrCreateOnfidoApplication={
                                    this.updateOrCreateOnfidoApplication
                                }
                                sdkToken={this.state.sdkToken}
                                guestCardGuid={this.props.guestCardGuid}
                                propertyInfo={this.props.propertyInfo}
                            />
                        ) : (
                            <p className="text-danger">Guest Card not found</p>
                        ))}
                    <form
                        ref={form => (this.formControl = form)}
                        noValidate={true}
                        onSubmit={this.onSubmit}
                        className={this.state.wasFormValidated ? 'was-validated' : ''}
                    >
                        <div className="navigation-buttons">
                            <button
                                className="btn btn-primary font-weight-bold mb-4"
                                hidden={
                                    this.state.checkStatus !==
                                    OnfidoIdVerificationStatuses.CheckPassed
                                }
                                type="submit"
                            >
                                Next
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    private async startIdCheck() {
        this.setState({
            ...this.state,
            isLoading: true,
            warningText: 'Please do not leave or refresh the page while beginning check'
        });
        let result = await startOnfidoIdCheck();

        if (result.success) {
            this.setState({
                ...this.state,
                errorMessage: undefined,
                checkStatus: result.data!.result,
                failedReason: result.data!.failReason,
                isLoading: false,
                warningText: undefined
            });
        } else {
            this.setState({
                ...this.state,
                checkStatus: OnfidoIdVerificationStatuses.ErrorOccurred,
                errorMessage: result.error,
                isLoading: false,
                warningText: undefined
            });
        }
    }

    private async updateOrCreateOnfidoApplication(birthdate: string) {
        let result = await updateOnfidoApplicant(birthdate);
        if (result.success) {
            this.setState({
                sdkToken: result.data!.sdkToken,
                checkStatus: result.data!.result,
                failedReason: result.data!.failReason,
                errorMessage: undefined,
                setBirthDateIsVisible: false
            });
        } else {
            this.setState({
                errorMessage: result.error
            });
        }
    }

    private async loadCheckStatus() {
        let result = await getOnfidoCheckStatus();

        if (result.success) {
            if (result.data) {
                this.props.updateVerificationStatus(
                    result.data.result === OnfidoIdVerificationStatuses.CheckPassed
                );
            }
            this.setState({
                ...this.state,
                checkStatus: result.data!.result,
                failedReason: result.data!.failReason
            });
        }
    }

    private async loadPhoneNumber() {
        let result = await getProspectUserProfile();
        if (result.success && result.data) {
            this.setState({
                prospectUserPhone: result.data.phone
            });
        }
    }

    private onSubmit(e: SyntheticEvent<HTMLFormElement>) {
        e.preventDefault();
        e.stopPropagation();

        this.props.goForward();
    }
}

export default OnfidoVerification;



// WEBPACK FOOTER //
// ./src/components/pages/prospect/signup/verification/OnfidoVerification.tsx